import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { ArrowUp, Square } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Textarea } from "@/components/ui/Textarea";

interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  onStop?: () => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  className?: string;
}

export interface ComposerHandle {
  focus: () => void;
}

export const Composer = forwardRef<ComposerHandle, ComposerProps>(
  function Composer(
    {
      value,
      onChange,
      onSubmit,
      onStop,
      disabled = false,
      loading = false,
      placeholder = "Ask a question…",
      className = "",
    },
    ref,
  ) {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }));

    useEffect(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
    }, [value]);

    const canSubmit = !disabled && !loading && value.trim().length > 0;

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSubmit) return;
      onSubmit(value.trim());
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        if (canSubmit) onSubmit(value.trim());
      }
    };

    // Operational-profile composer: shallow rounded card on the cool-gray
    // canvas, NDC Red send button. Focus state inherits the brand focus ring
    // from index.css.
    return (
      <form
        onSubmit={handleSubmit}
        className={`z-20 mx-auto w-[min(760px,calc(100%-32px))] pb-6 max-[680px]:w-[calc(100%-18px)] max-[680px]:pb-3 ${className}`}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="rounded-xl border border-border bg-surface p-[10px] focus-within:border-ndc-red focus-within:ring-2 focus-within:ring-ndc-red/30">
          <div className="flex items-end gap-2">
            <Textarea
              ref={textareaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              autoComplete="off"
              spellCheck
              rows={1}
              aria-label="Message NDC Knowledge Assistant"
            />
            {loading && onStop ? (
              <Button
                type="button"
                size="send"
                variant="outline"
                onClick={onStop}
                aria-label="Stop generating"
                title="Stop generating"
                className="border-ndc-red text-ndc-red hover:bg-ndc-red-tint"
              >
                <Square className="h-[14px] w-[14px]" fill="currentColor" strokeWidth={0} />
              </Button>
            ) : (
              <Button
                type="submit"
                size="send"
                variant="primary"
                disabled={!canSubmit}
                aria-label="Send"
              >
                <ArrowUp className="h-[18px] w-[18px]" strokeWidth={2.4} />
              </Button>
            )}
          </div>
        </div>
      </form>
    );
  },
);
